import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { LogOut, Building2, Users, Calendar, MapPin, BarChart3, Loader2, Shield, Wrench, ClipboardCheck } from 'lucide-react';
import { API_URL, authApi } from '@/lib/api';
import LayoutEditor from './LayoutEditor';
import RegistrationDashboard from '@/components/RegistrationDashboard';

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [booths, setBooths] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [events, setEvents] = useState([]);
  const [tab, setTab] = useState('overview');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!authApi.isAuthenticated()) {
      navigate('/login');
      return;
    }
    if (!authApi.isAdmin()) {
      alert('Admin access required');
      navigate('/');
      return;
    }
    const fetchData = async () => {
      setLoading(true);
      try {
        const [s, b, c, e] = await Promise.all([
          axios.get(`${API_URL}/stats`),
          axios.get(`${API_URL}/booths`),
          axios.get(`${API_URL}/companies`),
          axios.get(`${API_URL}/events`),
        ]);
        setStats(s.data.data);
        setBooths(b.data.data || []);
        setCompanies(c.data.data || []);
        setEvents(e.data.data || []);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [navigate]);

  const handleLogout = () => {
    authApi.logout();
    navigate('/login');
  };

  const user = authApi.getUser();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-950">
        <Loader2 className="w-12 h-12 text-emerald-400 animate-spin" />
      </div>
    );
  }

  const statCards = [
    { label: 'Total Booths', value: stats?.total_booths, icon: MapPin, color: 'from-blue-500 to-cyan-500' },
    { label: 'Occupied', value: stats?.occupied_booths, icon: Building2, color: 'from-emerald-500 to-teal-500' },
    { label: 'Available', value: stats?.available_booths, icon: MapPin, color: 'from-slate-500 to-slate-600' },
    { label: 'Exhibitors', value: stats?.total_companies, icon: Users, color: 'from-purple-500 to-pink-500' },
    { label: 'Events', value: stats?.total_events, icon: Calendar, color: 'from-orange-500 to-red-500' },
    { label: 'Sheltered', value: stats?.sheltered_booths, icon: MapPin, color: 'from-emerald-500 to-green-500' },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-emerald-950">
      {/* Header */}
      <header className="sticky top-0 z-40 bg-slate-900/90 backdrop-blur-xl border-b border-emerald-500/20">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center gap-4">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-emerald-500 to-emerald-700 flex items-center justify-center">
            <Shield className="w-5 h-5 text-white" />
          </div>
          <div className="flex-1">
            <div className="text-emerald-400 text-xs font-bold uppercase tracking-wider">Admin Dashboard</div>
            <div className="text-white text-lg font-bold">Hari Landskap Negara 2026</div>
          </div>
          <div className="hidden md:block text-right text-xs">
            <div className="text-slate-300 font-semibold" data-testid="admin-user-name">{user?.name}</div>
            <div className="text-slate-500">{user?.email}</div>
          </div>
          <Link to="/" className="px-3 py-2 rounded-lg bg-slate-800 hover:bg-slate-700 text-white text-sm">View Site</Link>
          <button onClick={handleLogout} className="px-3 py-2 rounded-lg bg-red-500/20 hover:bg-red-500/30 text-red-300 text-sm flex items-center gap-1" data-testid="logout-btn">
            <LogOut className="w-4 h-4" /> Logout
          </button>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 py-6">
        {/* Stats grid */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-6">
          {statCards.map(({ label, value, icon: Icon, color }) => (
            <div key={label} className="bg-slate-900/60 backdrop-blur rounded-2xl p-4 border border-slate-700/50">
              <div className={`w-10 h-10 rounded-lg bg-gradient-to-br ${color} flex items-center justify-center mb-2`}>
                <Icon className="w-5 h-5 text-white" />
              </div>
              <div className="text-3xl font-black text-white">{value || 0}</div>
              <div className="text-slate-400 text-xs mt-1">{label}</div>
            </div>
          ))}
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-4 border-b border-slate-800">
          {[
            { key: 'overview', label: 'Overview', icon: BarChart3 },
            { key: 'registration', label: 'Registration', icon: ClipboardCheck },
            { key: 'booths', label: 'Booths', icon: MapPin },
            { key: 'companies', label: 'Exhibitors', icon: Users },
            { key: 'events', label: 'Events', icon: Calendar },
            { key: 'layout', label: 'Layout Editor', icon: Wrench },
          ].map(({ key, label, icon: Icon }) => (
            <button
              key={key}
              onClick={() => setTab(key)}
              className={`px-4 py-2.5 flex items-center gap-2 text-sm font-semibold transition-all border-b-2 ${
                tab === key ? 'text-emerald-400 border-emerald-500' : 'text-slate-400 border-transparent hover:text-white'
              }`}
              data-testid={`admin-tab-${key}`}
            >
              <Icon className="w-4 h-4" /> {label}
            </button>
          ))}
        </div>

        {/* Content */}
        <div className="bg-slate-900/60 backdrop-blur rounded-2xl border border-slate-700/50 overflow-hidden">
          {tab === 'overview' && (
            <div className="p-6">
              <h2 className="text-white text-lg font-bold mb-3">Overview</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="p-4 rounded-xl bg-slate-800/50 border border-slate-700/50">
                  <div className="text-slate-400 text-xs uppercase mb-1">Occupancy Rate</div>
                  <div className="text-white text-3xl font-black">
                    {stats?.total_booths ? Math.round((stats.occupied_booths / stats.total_booths) * 100) : 0}%
                  </div>
                  <div className="w-full bg-slate-700 rounded-full h-2 mt-2">
                    <div className="bg-gradient-to-r from-emerald-500 to-emerald-400 h-2 rounded-full" 
                      style={{width: `${stats?.total_booths ? (stats.occupied_booths / stats.total_booths) * 100 : 0}%`}} />
                  </div>
                </div>
                <div className="p-4 rounded-xl bg-slate-800/50 border border-slate-700/50">
                  <div className="text-slate-400 text-xs uppercase mb-1">Shelter Coverage</div>
                  <div className="text-white text-3xl font-black">
                    {stats?.total_booths ? Math.round((stats.sheltered_booths / stats.total_booths) * 100) : 0}%
                  </div>
                  <div className="text-slate-400 text-xs mt-2">{stats?.sheltered_booths} of {stats?.total_booths} booths sheltered</div>
                </div>
              </div>
            </div>
          )}

          {tab === 'booths' && (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-800/50 text-slate-400 text-xs uppercase">
                  <tr>
                    <th className="text-left p-3">Booth #</th>
                    <th className="text-left p-3">Company</th>
                    <th className="text-left p-3">Zone</th>
                    <th className="text-left p-3">Category</th>
                    <th className="text-left p-3">Status</th>
                    <th className="text-right p-3">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-800">
                  {booths.map((b) => (
                    <tr key={b.id} className="hover:bg-slate-800/30" data-testid={`admin-booth-row-${b.id}`}>
                      <td className="p-3 text-white font-mono font-bold">{b.booth_number}</td>
                      <td className="p-3 text-slate-300">{b.company?.name || <em className="text-slate-500">Available</em>}</td>
                      <td className="p-3 text-slate-400">{b.hall?.name}</td>
                      <td className="p-3">
                        <span className="text-xs px-2 py-0.5 rounded-full text-white" style={{backgroundColor: b.category?.color || '#666'}}>
                          {b.category?.name}
                        </span>
                      </td>
                      <td className="p-3">
                        <span className={`text-xs px-2 py-0.5 rounded-full ${
                          b.status === 'occupied' ? 'bg-emerald-500/20 text-emerald-300' :
                          b.status === 'available' ? 'bg-slate-500/20 text-slate-300' :
                          'bg-amber-500/20 text-amber-300'
                        }`}>{b.status}</span>
                      </td>
                      <td className="p-3 text-right">
                        <Link to={`/booth/${b.id}`} className="text-emerald-400 hover:text-emerald-300 text-xs font-semibold">View</Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {tab === 'companies' && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 p-4">
              {companies.map((c) => (
                <div key={c.id} className="p-4 rounded-xl bg-slate-800/50 border border-slate-700/50" data-testid={`admin-company-${c.id}`}>
                  <div className="flex items-start justify-between mb-2">
                    <h3 className="text-white font-bold text-sm flex-1">{c.name}</h3>
                    {c.is_premium && <span className="text-yellow-400 text-xs">★</span>}
                  </div>
                  <div className="text-slate-400 text-xs mb-2">{c.country}</div>
                  <div className="text-slate-500 text-xs line-clamp-2">{c.description}</div>
                  <div className="flex gap-1 mt-3 text-[10px] flex-wrap">
                    {c.is_featured && <span className="px-2 py-0.5 rounded bg-blue-500/20 text-blue-300">Featured</span>}
                    {c.is_sponsor && <span className="px-2 py-0.5 rounded bg-purple-500/20 text-purple-300">Sponsor</span>}
                    {c.is_new && <span className="px-2 py-0.5 rounded bg-emerald-500/20 text-emerald-300">New</span>}
                  </div>
                </div>
              ))}
            </div>
          )}

          {tab === 'events' && (
            <div className="divide-y divide-slate-800">
              {events.map((ev) => (
                <div key={ev.id} className="p-4 hover:bg-slate-800/30" data-testid={`admin-event-${ev.id}`}>
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-xs px-2 py-0.5 rounded-full bg-slate-700 text-slate-300 uppercase">{ev.type}</span>
                        {ev.is_featured && <span className="text-xs text-yellow-400">★ Featured</span>}
                      </div>
                      <h3 className="text-white font-bold">{ev.title}</h3>
                      <div className="text-slate-400 text-xs mt-1">
                        {new Date(ev.start_time).toLocaleString()} → {new Date(ev.end_time).toLocaleString()}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {tab === 'registration' && (
            <RegistrationDashboard
              companies={companies}
              onUpdate={(u) => setCompanies((cur) => cur.map((c) => (c.id === u.id ? { ...c, ...u } : c)))}
            />
          )}

          {tab === 'layout' && <LayoutEditor />}
        </div>
      </div>
    </div>
  );
}
